import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { isValid, isValidWithoutData } from 'clients/Clients';
import { collectEvent } from 'clients/MonitorClient';
import { deleteItemWishlist, updateWishlist } from 'clients/WishlistClient';
import CardImage from 'components-v2/mocules/CardImage';
import { COMBO_SUGGESTED_BY } from 'components-v2/mocules/Cart/CartItem/ComboSuggestion';
import ImageSlider from 'components-v2/mocules/ImageSlider';
import QuantityControl from 'components-v2/mocules/QuantityControl';
import ProductLimitMessage from 'components/mocules/ProductLimitMessage';
import { HTTP_STATUS } from 'constants/Enums/http';
import { SHOPPING_CART_ICON } from 'constants/Images';
import { useCart } from 'context/Cart/CartContext';
import useMultiImageBox from 'hooks/useMultiImageBox';
import useToggle from 'hooks/useToggle';
import useTrackingMonitorSubstituteProduct from 'hooks/useTrackingMonitorSubstituteProduct';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import WebService from 'services/WebService';
import { getSKUErrorMessage } from 'services/convertNewAPIProduct';
import { ImageFallbackStatic } from 'utils/ImageFallback';
import { mapScreenToEnum, mapSourceToEnum, MONITORING_COLLECTOR_TYPE } from 'utils/MonitorUtils';
import NotifyUtils from 'utils/NotifyUtils';
import gtag from 'utils/gtag';
import { useStore } from 'zustand-lib/storeGlobal';
import useCartQuantity from 'zustand-lib/useCartQuantity';
import styles from './styles.module.css';

const PopupCreateTicket = dynamic(() => import('components/organisms/TicketListV2/PopupCreate'), { ssr: false });
const PopupMultilImage = dynamic(() => import('components-v2/mocules/PopupMultilImage'), { ssr: false });

function InfoBuy({ orderedCount }) {
  const { score } = useCartQuantity((state) => state);

  return (
    <div className={styles.buyInfo}>
      <div className={styles.countBuy}>
        {orderedCount ? (
          <>
            <ImageFallbackStatic src={SHOPPING_CART_ICON} width={16} height={16} alt="cart_icon" />
            <Typography className={styles.numberBuy}>{orderedCount}</Typography>{' '}
            <Typography className={styles.typography}> Lượt mua trong 24 giờ</Typography>
          </>
        ) : null}
      </div>
      <Typography className={styles.score}> {+score !== 0 ? `${score} điểm` : ''}</Typography>
    </div>
  );
}

export default function LeftContent({ imagesProxy, orderedCount, user, product }) {
  const [imageActive, setImageActive] = useState(0);
  const [openModal, setIsOpenModal] = useState(false);
  const [isWish, setIsWish] = useState(product?.isWishList);
  const { selectedImage, handleImageSelection, handlePrevious, handleNext } = useMultiImageBox({ images: imagesProxy });
  const { open, handleOpen, handleClose } = useToggle();

  const cart = useCart();
  const router = useRouter();
  const { trackingMonitorSubstituteAddToCart } = useTrackingMonitorSubstituteProduct();
  const { isSKUReplace = false, sellerIdMainSKU, productIdMainSKU, mainSKU, mainType } = router.query || {};

  // Keep the initial qty and update the qty when changing the QuantityControl
  // onCleanup, send addToCart GA event
  let initialQty = useRef(0);
  let qty = useRef(0);
  let skuProductSuggestFrom = useRef();
  let comboSKUCode = useRef('');

  // TODO: should detect if product is buyable or not, instead of using errorMessageProduct to return product is buyable or not
  const appSetting = useStore((state) => state.appSetting);
  const errorMessageProduct = getSKUErrorMessage(product, appSetting);

  useEffect(() => {
    if (cart.loading) return;

    const comboProduct = cart.cartItems?.find((item) => item.sku === product.sku);
    initialQty.current = comboProduct?.quantity;
  }, [cart.loading]);

  useEffect(() => {
    if (cart.loading) return;
    if (!skuProductSuggestFrom.current) return;

    const comboProduct = cart.cartItems?.find((item) => item.sku === product.sku);
    qty.current = comboProduct?.quantity;
  }, [cart]);

  useEffect(() => {
    if (product.sku) comboSKUCode.current = product.sku;
  }, [product.sku]);

  useEffect(() => {
    (async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const slug = searchParams.get(COMBO_SUGGESTED_BY);
      if (!slug) return;

      const productRes = await WebService.getDataProductDetail({ slug });
      skuProductSuggestFrom.current = productRes.data?.[0];
    })();

    return () => {
      if (skuProductSuggestFrom.current && qty.current !== initialQty.current) {
        gtag.addToCartComboSuggestion({
          user,
          skuProduct: skuProductSuggestFrom.current,
          quantity: qty.current,
          comboSKUCode: comboSKUCode.current || product.sku,
        });

        collectEvent(
          MONITORING_COLLECTOR_TYPE.ADD_SUGGESTED_COMBO_TO_CART,
          {
            quantity: qty.current,
            combo: comboSKUCode.current || product.sku,
            sku: skuProductSuggestFrom.current.sku,
            seller_id: skuProductSuggestFrom.current.sellerID,
            product_id: skuProductSuggestFrom.current.productId,

            source: mapSourceToEnum(),
            screen: mapScreenToEnum(),
            content: 'Add Combo gợi ý vào Giỏ hàng',
          },
          { quantity: qty.current },
        );
      }
    };
  }, []);

  const handleOpenImageModal = (index) => {
    setImageActive((indexActive) => {
      if (index) return index;
      return indexActive;
    });
    handleOpen();
  };

  useEffect(() => {
    setImageActive(selectedImage);
  }, [selectedImage]);

  const handleSetImageActive = (callback) => {
    handleImageSelection(callback);
    setImageActive(callback);
  };

  const handleClickQuestion = () => {
    setIsOpenModal(true);
  };

  const handleDeleteProductWishlist = async (product) => {
    const res = await deleteItemWishlist(product?.sku);
    if (isValidWithoutData(res)) {
      setIsWish(() => false);
      return NotifyUtils.success(`Đã xóa ${product?.name} khỏi danh sách quan tâm`);
    }
    return NotifyUtils.error(`Xóa sản phẩm ${product?.name} khỏi danh sách quan tâm thất bại`);
  };

  const handleUpdateWishlist = async (product) => {
    const res = await updateWishlist(product?.sku);
    if (isValid(res) || res?.status === HTTP_STATUS.Existed) {
      setIsWish(() => true);
      return NotifyUtils.success(`Đã thêm ${product?.name} vào danh sách quan tâm`);
    }
    return NotifyUtils.error(`Thêm ${product?.name} vào danh sách sản phẩm quan tâm thất bại`);
  };

  const handleClickWhistList = async () => {
    if (isWish) {
      handleDeleteProductWishlist(product);
    } else {
      handleUpdateWishlist(product);
    }
  };

  const handleEventTracking = (product) => {
    if (isSKUReplace) {
      trackingMonitorSubstituteAddToCart({
        sku: product?.sku,
        sellerId: product?.sellerID,
        productId: product?.productId,
        type: product?.status === 'NORMAL' ? 'Instock' : product?.status,
        pathname: router.pathname,
        sellerIdMainSKU: sellerIdMainSKU,
        productIdMainSKU: productIdMainSKU,
        mainSKU: mainSKU,
        mainType: mainType || 'NORMAL',
      });
    }
  };

  return (
    <>
      <Grid className={styles.imageShow} item xs={12}>
        <div className={styles.wrapperImage}>
          <CardImage
            img={imagesProxy[imageActive || 0]}
            onClickQuestion={handleClickQuestion}
            onClickWhistList={handleClickWhistList}
            onClickImage={handleOpenImageModal}
            isOpenModal={open}
            isWish={isWish}
            hiddenIconAndText={!user}
            isNotAuth={!!user}
            alt={product?.name}
          />
          {imagesProxy?.length > 1 && (
            <ImageSlider
              className={styles.imageSlider}
              images={imagesProxy}
              imageActive={imageActive}
              isOpenModal={open}
              setImageActive={handleSetImageActive}
              onClickImage={handleOpenImageModal}
              alt={product?.name}
            />
          )}
        </div>
        {!errorMessageProduct && user && (
          <>
            <InfoBuy orderedCount={orderedCount} displayPrice={product?.displayPrice} />
            {product?.status !== 'OUT_OF_STOCK' && (
              <QuantityControl
                key={product?.sku}
                product={product}
                onChange={() => {
                  handleEventTracking(product);
                }}
                onAdd={() => {
                  handleEventTracking(product);
                }}
              />
            )}
            <div className={styles.typography}>
              <ProductLimitMessage consumedMaxQuantity={product.consumedMaxQuantity} unit={product.unit} page="product" />
            </div>
          </>
        )}
      </Grid>
      {openModal && (
        <PopupCreateTicket
          user={user}
          product={product}
          productName={product.name}
          productId={product.productId}
          sku={product.sku}
          visible
          onClose={() => setIsOpenModal(false)}
          ticketType="PRODUCT"
        />
      )}
      {open && (
        <PopupMultilImage
          images={imagesProxy}
          open
          handleClose={handleClose}
          imageName={product?.name}
          selectedImage={imageActive}
          handleClickImage={handleImageSelection}
          handlePrevious={handlePrevious}
          handleNext={handleNext}
        />
      )}
    </>
  );
}
